import { isIos, isStandalone } from '@/shared/utils/device'
import { readStorage, writeStorage } from '@/shared/utils/storage'
import { settingsApi, type PushDevice } from './api'

/**
 * Web push в браузере (ТЗ 18.7, задача 3.15). Разрешение спрашивается только по кнопке.
 * Подписка этого устройства сверяется с сервером при каждом запуске: браузер мог её
 * сбросить или сменить, тогда она оформляется заново.
 */
export type PushState =
  /** Браузер не умеет push или страница открыта не по HTTPS. */
  | 'unsupported'
  /** iPhone и iPad: push только в приложении, добавленном на экран «Домой». */
  | 'needs-install'
  | 'denied'
  | 'off'
  | 'on'

/** Устройство включало push: если подписка пропала, её нужно восстановить. */
const ENABLED_KEY = 'push.enabled'

function supported(): boolean {
  return (
    window.isSecureContext &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  // Не ждём бесконечно: в разработке без service worker `ready` не наступает.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
  ])
}

export async function browserSubscription(): Promise<PushSubscription | null> {
  if (!supported()) return null
  const reg = await registration()
  return (await reg?.pushManager.getSubscription()) ?? null
}

export async function pushState(): Promise<PushState> {
  if (!supported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported'
  if (Notification.permission === 'denied') return 'denied'
  if (Notification.permission !== 'granted') return 'off'
  return (await browserSubscription()) ? 'on' : 'off'
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const base64 = (value + '='.repeat((4 - (value.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const raw = atob(base64)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

function bytesToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return ''
  let raw = ''
  for (const byte of new Uint8Array(buffer)) raw += String.fromCharCode(byte)
  return btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function sameKey(subscription: PushSubscription, key: Uint8Array): boolean {
  const current = subscription.options.applicationServerKey
  if (!current) return true
  const bytes = new Uint8Array(current)
  return bytes.length === key.length && bytes.every((b, i) => b === key[i])
}

/** Название устройства по умолчанию для списка: браузер и система. */
export function defaultDeviceLabel(): string {
  const ua = navigator.userAgent
  const os = isIos()
    ? /iPad|Macintosh/.test(ua)
      ? 'iPad'
      : 'iPhone'
    : /Android/.test(ua)
      ? 'Android'
      : /Windows/.test(ua)
        ? 'Windows'
        : /Mac OS X/.test(ua)
          ? 'Mac'
          : /Linux/.test(ua)
            ? 'Linux'
            : ''
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /YaBrowser\//.test(ua)
      ? 'Яндекс Браузер'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : ''
  return [browser, os].filter(Boolean).join(', ')
}

async function register(subscription: PushSubscription, label: string | null): Promise<PushDevice> {
  return settingsApi.subscribePush({
    endpoint: subscription.endpoint,
    keys: {
      p256dh: bytesToBase64Url(subscription.getKey('p256dh')),
      auth: bytesToBase64Url(subscription.getKey('auth')),
    },
    deviceLabel: label,
  })
}

async function subscribe(): Promise<PushSubscription> {
  const reg = await registration()
  if (!reg) throw new Error('service worker не запущен')
  const key = base64UrlToBytes((await settingsApi.pushPublicKey()).key)
  const existing = await reg.pushManager.getSubscription()
  // Ключ сервера сменился: старая подписка ему не подходит.
  if (existing && !sameKey(existing, key)) await existing.unsubscribe()
  else if (existing) return existing
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
}

/**
 * Включить push на этом устройстве по нажатию кнопки. Возвращает итоговое состояние:
 * отказ в разрешении не ошибка, а `denied`.
 */
export async function enablePush(label: string | null): Promise<PushState> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off'
  const subscription = await subscribe()
  await register(subscription, label)
  writeStorage(ENABLED_KEY, '1')
  return 'on'
}

/** Отписать это устройство: на сервере и в браузере. */
export async function disablePush(devices: readonly PushDevice[]): Promise<void> {
  writeStorage(ENABLED_KEY, '0')
  const subscription = await browserSubscription()
  if (!subscription) return
  const device = devices.find((d) => d.endpoint === subscription.endpoint)
  if (device) await settingsApi.unsubscribePush(device.id)
  await subscription.unsubscribe()
}

/**
 * Сверка при запуске (ТЗ 18.7): подписка браузера есть на сервере, а пропавшая или
 * с другим ключом оформляется заново. Без разрешения ничего не спрашивает.
 */
export async function syncPush(): Promise<void> {
  if (!supported() || Notification.permission !== 'granted') return
  if (readStorage(ENABLED_KEY) === '0') return
  const current = await browserSubscription()
  if (!current && readStorage(ENABLED_KEY) !== '1') return
  const subscription = await subscribe()
  const devices = await settingsApi.pushDevices()
  const known = devices.find((d) => d.endpoint === subscription.endpoint)
  if (!known || !known.active || subscription !== current) {
    await register(subscription, known?.deviceLabel ?? defaultDeviceLabel())
  }
  writeStorage(ENABLED_KEY, '1')
}
